import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import {
  gridClasses,
  GridColDef,
  GridRenderCellParams,
  gridPaginationModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import MedicationIcon from "@mui/icons-material/Medication";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { grey } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { Button, IconButton } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import moment from "moment/moment";
import axios from "axios";
import {
  formatPrice,
  getPackTypeDisplay,
  getSealDetailsDisplay,
  getOptionalPackageConditionTooltip,
} from "../../components/utils/utils";
import BuyAction from "./BuyAction";

export const PRODUCT_LIST_PAGE_SIZE = 10;

export const BuyGridPagination = (props: { rowCount?: number }) => {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const totalPages = Math.max(Math.ceil((props.rowCount ?? 0) / (paginationModel.pageSize || PRODUCT_LIST_PAGE_SIZE)), 1);
  const currentPage = paginationModel.page + 1;
  const windowStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index);
  const shouldShowLastPage = totalPages > 3 && windowEnd < totalPages;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        px: 2,
        py: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Page {currentPage} of {totalPages}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" onClick={() => apiRef.current.setPage(0)} disabled={isFirstPage}>
          <FirstPageRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => apiRef.current.setPage(currentPage - 2)} disabled={isFirstPage}>
          <KeyboardArrowLeftRoundedIcon fontSize="small" />
        </IconButton>

        {visiblePages.map((page) => {
          const isSelected = currentPage === page;
          return (
            <Button
              key={page}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(page - 1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "8px",
                fontWeight: 600,
                p: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: alpha(theme.palette.primary.main, 0.35),
                ...(isSelected && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }),
              }}
            >
              {page}
            </Button>
          );
        })}
        {shouldShowLastPage && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              ...
            </Typography>
            <Button
              size="small"
              variant={currentPage === totalPages ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(totalPages - 1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "8px",
                fontWeight: 600,
                p: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: alpha(theme.palette.primary.main, 0.35),
                ...(currentPage === totalPages && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }),
              }}
            >
              {totalPages}
            </Button>
          </>
        )}

        <IconButton size="small" onClick={() => apiRef.current.setPage(currentPage)} disabled={isLastPage}>
          <KeyboardArrowRightRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => apiRef.current.setPage(totalPages - 1)} disabled={isLastPage}>
          <LastPageRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export const DrugCellRenderer = ({ params }: { params: GridRenderCellParams<any> }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const drugNdc = params?.row?.ndc;

  useEffect(() => {
    if (!drugNdc) return;
    const formattedNdc = drugNdc.replace(/-/g, "");
    setIsLoading(true);
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
    axios
      .get(imageUrlApi)
      .then((response) => {
        setImageUrl(response.data.imageUrl);
      })
      .catch((error) => {
        console.error("Error fetching drug image:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [drugNdc]);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {isLoading ? (
        <Box
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            marginRight: "8px",
            bgcolor: "grey.300",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt="Drug"
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            marginRight: "8px",
          }}
        />
      ) : (
        <MedicationIcon
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            marginRight: "8px",
            color: "#ccc",
          }}
        />
      )}
      <span>{params.value}</span>
    </Box>
  );
};

export function mapProductsToGridRows(products: any[] | undefined | null) {
  return (
    products?.map((product: any) => ({
      ...product,
      expirationDate: moment(product.expDate),
      available: product.quantity,
      priceUnit: Number(product.unitPrice),
      price: Number(product.price),
      packaging: product.quantityPerPack,
      packagingValue: product.packaging,
      packType: product.type,
      sealed: product.packageCondition,
      sealDetails: product.sealDetails,
    })) ?? []
  );
}

export function getProductListGridColumns(options: {
  currentUserId: number;
  updateCartLoading: boolean;
  handleCart: (params: any, packageAvailable: number) => void;
}): GridColDef[] {
  const { currentUserId, updateCartLoading, handleCart } = options;

  return [
    {
      field: "drugName",
      headerName: "Drug name",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      disableExport: true,
      disableReorder: true,
      hideable: false,
      minWidth: 300,
      cellClassName: "stickyCol",
      headerClassName: "stickyCell",
      renderCell: (params: GridRenderCellParams<any>) => <DrugCellRenderer params={params} />,
    },
    {
      field: "strength",
      headerName: "Strength",
      editable: false,
      disableColumnMenu: true,
      sortable: false,
      minWidth: 150,
    },
    {
      field: "ndc",
      headerName: "NDC",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 150,
    },
    {
      field: "packaging",
      headerName: "Packaging",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 120,
    },
    {
      field: "packType",
      headerName: "Pack Type",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 150,
      renderCell: (params: any) => getPackTypeDisplay(params.value, params.row.packagingValue),
    },
    {
      field: "sealed",
      headerName: "Seal Details",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 260,
      flex: 0.9,
      renderCell: (params: any) => {
        const isNonSealed = params.row.packageCondition !== 1;
        const baseText = isNonSealed ? "Non-Sealed (See Details)" : "Sealed (See Details)";
        const sealDetailText = params.row.sealDetails
          ? getSealDetailsDisplay(params.row.sealDetails)
          : null;
        const optionalPackageConditionTooltip = getOptionalPackageConditionTooltip(params.row, {
          shortenOtherLabel: true,
        });

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              width: "100%",
              minWidth: 0,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                {baseText}
              </Typography>
              {optionalPackageConditionTooltip && (
                <Tooltip
                  title={<Box sx={{ whiteSpace: "pre-line" }}>{optionalPackageConditionTooltip}</Box>}
                  arrow
                  placement="top"
                >
                  <WarningAmberOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: "warning.main",
                      cursor: "help",
                      flexShrink: 0,
                    }}
                    aria-label="Optional package condition details"
                  />
                </Tooltip>
              )}
            </Box>
            {sealDetailText && (
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
                {sealDetailText}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 150,
      type: "dateTime",
      valueFormatter: (params) => moment(params?.value).format("MM/YYYY"),
    },
    {
      field: "price",
      headerName: "Price",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
      renderCell: (params: any) => `$${formatPrice(params.value)}`,
    },
    {
      field: "priceUnit",
      headerName: "Price Unit",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
      renderCell: (params: any) => `$${formatPrice(params.value)}`,
    },
    {
      field: "available",
      headerName: "Available",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      minWidth: 110,
    },
    {
      field: "action",
      headerName: "",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 170,
      renderCell: (params: any) => {
        if (params.row.available < 1) {
          return null;
        }

        if (params.row.userId === currentUserId) {
          return (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center" }}>
              <Box
                sx={{
                  borderRadius: "16px",
                  padding: "4px 8px",
                  width: "100%",
                  textAlign: "center",
                  backgroundColor: grey[300],
                }}
              >
                my product
              </Box>
            </Box>
          );
        }

        return (
          <BuyAction
            rowParams={params}
            updateCartLoading={updateCartLoading}
            handleAvailableCart={(packageAvailable) => handleCart(params, packageAvailable)}
          />
        );
      },
    },
  ];
}

export const productListDataGridSx = {
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.row}:hover`]: {
    cursor: "pointer",
  },
  "& .MuiDataGrid-footerContainer": {
    minHeight: 56,
    borderTop: (theme: { palette: { primary: { main: string; light: string } } }) =>
      `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    backgroundColor: (theme: { palette: { primary: { light: string } } }) => alpha(theme.palette.primary.light, 0.25),
  },
};

export function attachStickyColumnScrollListener() {
  const handleScrollHorizontal = () => {
    const currentScrollPos = document.querySelector(".MuiDataGrid-virtualScroller")?.scrollLeft || 0;
    const firstColumnHeader = document.querySelector<HTMLElement>(".stickyCell");
    if (firstColumnHeader) {
      firstColumnHeader.style.transform = `translate3d(${currentScrollPos}px, 0px, 0px)`;
    }
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const findVirtualScroller = () => {
    const virtualScrollerElement = document.querySelector(".MuiDataGrid-virtualScroller");
    if (!virtualScrollerElement) {
      timer = setTimeout(findVirtualScroller, 100);
    } else {
      virtualScrollerElement.addEventListener("scroll", handleScrollHorizontal);
    }
  };
  findVirtualScroller();

  return () => {
    if (timer) clearTimeout(timer);
    const virtualScrollerElement = document.querySelector(".MuiDataGrid-virtualScroller");
    virtualScrollerElement?.removeEventListener("scroll", handleScrollHorizontal);
  };
}
